/**
 * What a package contains, in the words and groups the customer sees.
 *
 * The billing page is where a firm learns what it gets, now that the sidebar
 * simply hides what it has not licensed. PlanCard kept its own copy of the
 * section names, and it had drifted: `clients` instead of `contacts`, and no
 * Projects, Review, Drafting or Matter economics at all, so every card
 * undersold its package and AI keys such as `review_ai` showed raw. Names now
 * come from config/entitlementKeys, and the groups follow the sidebar's.
 *
 * A section this file does not place still shows, under "Other", with a
 * humanised name: a new grant key must never silently vanish from the page.
 */
import { LicensePlan } from '../../../store/actions/PlanActions';
import { aiFeatureLabel, sectionLabel } from '../../../config/entitlementKeys';

export interface CatalogueGroup {
  label: string;
  keys: string[];
}

export const SECTION_GROUPS: CatalogueGroup[] = [
  {
    label: 'Workspace',
    keys: ['dashboard', 'tasks', 'calendar', 'notifications', 'messages',
           'contacts', 'matters', 'files', 'projects'],
  },
  { label: 'Tools', keys: ['research', 'reviews', 'drafting', 'tables', 'marketplace'] },
  { label: 'Knowledge', keys: ['templates', 'data_sources', 'analytics'] },
  {
    label: 'Organization',
    keys: ['people', 'departments', 'teams', 'finance', 'matter_economics'],
  },
];

const PLACED = new Set(SECTION_GROUPS.flatMap((g) => g.keys));

export { sectionLabel, aiFeatureLabel };

export const planSections = (plan: LicensePlan): string[] =>
  plan.feature_gates?.sections ?? [];

export const planAiFeatures = (plan: LicensePlan): string[] =>
  plan.feature_gates?.ai_features ?? [];

/** The given sections in catalogue order, grouped; unplaced ones under "Other". */
export const groupSections = (sections: string[]): CatalogueGroup[] => {
  const have = new Set(sections);
  const groups = SECTION_GROUPS
    .map((g) => ({ label: g.label, keys: g.keys.filter((k) => have.has(k)) }))
    .filter((g) => g.keys.length > 0);
  const other = sections.filter((s) => !PLACED.has(s));
  if (other.length) groups.push({ label: 'Other', keys: other });
  return groups;
};

/** Every section and AI feature any of these plans or add-ons grants, grouped. */
export const catalogueRows = (
  plans: LicensePlan[]
): { sections: CatalogueGroup[]; aiFeatures: string[] } => {
  const sections = Array.from(new Set(plans.flatMap(planSections)));
  const ai = Array.from(new Set(plans.flatMap(planAiFeatures)));
  return { sections: groupSections(sections), aiFeatures: ai };
};

/**
 * Which plans to offer. A trial is for a firm that is starting out: it is
 * listed only while the org is on a trial or has no plan, never as a
 * "downgrade" from a paid package.
 */
export const offeredBasePlans = (
  plans: LicensePlan[],
  currentPlanType: string | null | undefined
): LicensePlan[] => {
  const showTrial = !currentPlanType || currentPlanType === 'trial';
  return plans
    .filter((p) => p.type === 'base' || (p.type === 'trial' && showTrial))
    .sort((a, b) => a.sort_order - b.sort_order);
};

/**
 * A plan price in the org's billing currency - "€199", "$85".
 *
 * Every price on the billing page used to be a hard-coded "$" while the org
 * could be billed in euro (`price_currency` from /my-access was passed down
 * and never used). No currency means EUR - the backend's own rule
 * (OrganizationLicense.price_currency: NULL is treated as EUR).
 */
export const formatPlanPrice = (amount: number, currency?: string | null, decimals = 0): string => {
  const code = (currency || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  } catch {
    return `${code} ${Math.round(amount)}`;
  }
};
